import { isNonEnglishLanguage } from "@/i18n";
import { api } from "@/services/api";
import { useAppDispatch } from "@/services/store";
import { hasAcceptedAIDisclaimer, setAIDisclaimerAccepted } from "@/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AIDisclaimerModal } from "./AIDisclaimerModal";

/**
 * Unified AI disclaimer that handles both startup and manual language changes.
 * Shows disclaimer AFTER language has changed, allowing content to load in target language.
 */
export function AIDisclaimerProvider() {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isStartupMode, setIsStartupMode] = useState(false);
  const hasInitializedRef = useRef(false);
  const previousLanguageRef = useRef<string>("en");

  useEffect(() => {
    const currentLanguage = i18n.resolvedLanguage;
    if (!currentLanguage) return;

    const isTranslatedLang = isNonEnglishLanguage(currentLanguage);
    const hasAcceptedLangAi = hasAcceptedAIDisclaimer(currentLanguage);

    const shouldShowDisclaimer = isTranslatedLang && !hasAcceptedLangAi;
    const isApplicationStartup = !hasInitializedRef.current;

    if (shouldShowDisclaimer) {
      setIsStartupMode(isApplicationStartup);
      setShowDisclaimer(true);
    }

    if (isApplicationStartup) {
      hasInitializedRef.current = true;
    }
    if (hasAcceptedLangAi || !isTranslatedLang) {
      // When switching language once accepted or back to english, update previous ref
      previousLanguageRef.current = currentLanguage;
    }
  }, [i18n.resolvedLanguage]);

  const handleAccept = async () => {
    const currentLanguage = i18n.resolvedLanguage;
    setAIDisclaimerAccepted(currentLanguage!);
    // When accepting new language, set previous ref to current
    previousLanguageRef.current = currentLanguage!;
    setShowDisclaimer(false);
  };

  const handleGoBack = async () => {
    setShowDisclaimer(false);

    // Go back to previous language - en if startup, otherwise the last accepted language
    const goBackLanguage = isStartupMode ? "en" : previousLanguageRef.current;
    await i18n.changeLanguage(goBackLanguage);

    dispatch(api.util.resetApiState());
  };

  return (
    <AIDisclaimerModal
      open={showDisclaimer}
      onAccept={handleAccept}
      onGoBack={handleGoBack}
      isStartupMode={isStartupMode}
    />
  );
}
